import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../lib/utils';
import { Button } from '../../atoms/Button';
import { Heading } from '../../atoms/Heading';

const panelVariants = cva(
  'flex flex-col border border-border bg-background rounded-lg',
  {
    variants: {
      padding: {
        none: '',
        sm: 'p-2',
        md: 'p-4',
        lg: 'p-6',
      },
    },
    defaultVariants: {
      padding: 'md',
    },
  }
);

export interface PanelContainerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof panelVariants> {
  title?: string;
  actions?: React.ReactNode;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  children: React.ReactNode;
}

/**
 * PanelContainer - Container with optional header, collapsible, actions
 */
export const PanelContainer = React.forwardRef<HTMLDivElement, PanelContainerProps>(
  (
    {
      title,
      actions,
      collapsible = false,
      defaultCollapsed = false,
      padding,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const [isCollapsed, setIsCollapsed] = React.useState(defaultCollapsed);

    return (
      <div
        ref={ref}
        className={cn(panelVariants({ padding }), className)}
        {...props}
      >
        {(title || actions || collapsible) && (
          <div className="flex items-center justify-between mb-4 pb-2 border-b border-border">
            <Heading level="h3" className="text-lg">
              {title}
            </Heading>
            <div className="flex items-center gap-2">
              {actions}
              {collapsible && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsCollapsed(!isCollapsed)}
                  aria-label={isCollapsed ? 'Expand panel' : 'Collapse panel'}
                >
                  {isCollapsed ? '▼' : '▲'}
                </Button>
              )}
            </div>
          </div>
        )}
        {!isCollapsed && <div className="flex-1">{children}</div>}
      </div>
    );
  }
);

PanelContainer.displayName = 'PanelContainer';
